// Thème Maison : cadeau ajouté automatiquement au panier.
// Règles lues dans la section « Cadeau automatique » (blocs « Règle cadeau »).
// - condition remplie et cadeau absent → on l'ajoute (quantité 1, propriété cachée _mx_gift = 1) ;
// - condition plus remplie → on le retire ;
// - la cliente l'a retiré elle-même → on ne le remet pas pendant la session (sessionStorage).
// Les requêtes préviennent le panier d'Horizon (même événement que le bouton d'achat) pour qu'il
// se mette à jour, sans ouvrir le tiroir tout seul.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { getCart } from '@theme/mx-cart';

const STORAGE_KEY = 'mx-auto-gift';
const GIFT_PROPERTY = '_mx_gift';

/**
 * @typedef {{ variantId: string, available: boolean, condition: 'amount' | 'count', threshold: number }} GiftRule
 * @typedef {{ token: string, seen: string[], dismissed: string[] }} GiftMemory
 */

/** Lit le panier une seule fois quand plusieurs blocs le demandent au même moment. */
function readCart() {
  const store = /** @type {any} */ (window);
  if (!store.__mxCartRead) {
    store.__mxCartRead = getCart().finally(() => {
      setTimeout(() => {
        store.__mxCartRead = null;
      }, 0);
    });
  }
  return store.__mxCartRead;
}

function currencyRate() {
  const rate = Number(/** @type {any} */ (window).Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

/** Sections panier présentes sur la page (tiroir, page panier), pour les re-rendre d'un coup. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

/** @param {any} item */
function isGiftLine(item) {
  return Boolean(item?.properties && item.properties[GIFT_PROPERTY]);
}

/** @param {string} token @returns {GiftMemory} */
function loadMemory(token) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && saved.token === token) {
      return { token, seen: saved.seen ?? [], dismissed: saved.dismissed ?? [] };
    }
  } catch (error) {
    // Stockage indisponible (navigation privée stricte) : on repart de zéro.
  }
  return { token, seen: [], dismissed: [] };
}

/** @param {GiftMemory} memory */
function saveMemory(memory) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch (error) {
    // Rien à faire : la règle « ne pas remettre » ne tiendra que le temps de la page.
  }
}

class MxAutoGift extends HTMLElement {
  #abort = new AbortController();
  #busy = false;
  #pending = false;
  /** @type {Set<string>} variantes refusées par Shopify (épuisées…) : on n'insiste pas */
  #failed = new Set();

  connectedCallback() {
    if (this.dataset.designMode === 'true' || this.dataset.enabled !== 'true') return;

    const { signal } = this.#abort;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent, { signal });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartEvent, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    this.#check();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  /** @param {any} event */
  #onCartEvent = (event) => {
    // Nos propres requêtes relancent déjà un contrôle (voir #pending) : inutile de doubler.
    if (event?.target === this) return;
    (event?.promise ?? Promise.resolve())
      .then(() => this.#check())
      .catch(() => {
        // Le changement de panier a échoué : rien à recalculer.
      });
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#check();
  };

  /** @returns {GiftRule[]} */
  get #rules() {
    const rate = currencyRate();
    return [...this.querySelectorAll('[data-mx-gift-rule]')]
      .filter((el) => el instanceof HTMLElement)
      .map((el) => {
        const element = /** @type {HTMLElement} */ (el);
        const condition = element.dataset.condition === 'count' ? 'count' : 'amount';
        const raw = Number(element.dataset.threshold || 0);
        return {
          variantId: element.dataset.variantId || '',
          available: element.dataset.available === 'true',
          condition: /** @type {'amount' | 'count'} */ (condition),
          threshold: condition === 'amount' ? Math.round(raw * rate) : raw,
        };
      })
      .filter((rule) => rule.variantId && rule.available && rule.threshold > 0);
  }

  async #check() {
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;
    try {
      const cart = await readCart();
      await this.#apply(cart);
    } catch (error) {
      console.warn('[mx-auto-gift]', error);
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#check();
      }
    }
  }

  /** @param {any} cart */
  async #apply(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const giftLines = items.filter(isGiftLine);
    const baseLines = items.filter((item) => !isGiftLine(item));

    const giftTotal = giftLines.reduce((sum, item) => sum + (item.final_line_price ?? 0), 0);
    const amount = Math.max(0, (cart.total_price ?? 0) - giftTotal);
    const count = baseLines.reduce((sum, item) => sum + (item.quantity ?? 0), 0);

    let metRules = baseLines.length
      ? this.#rules.filter((rule) => (rule.condition === 'count' ? count : amount) >= rule.threshold)
      : [];
    if (this.dataset.single === 'true') metRules = metRules.slice(-1);
    const wanted = [...new Set(metRules.map((rule) => rule.variantId))];

    const memory = loadMemory(String(cart.token ?? ''));
    const present = new Set(giftLines.map((item) => String(item.variant_id)));

    // Vu au dernier passage, toujours mérité, mais disparu : c'est la cliente qui l'a retiré.
    for (const id of memory.seen) {
      if (!present.has(id) && wanted.includes(id) && !memory.dismissed.includes(id)) {
        memory.dismissed.push(id);
      }
    }

    /** @type {Record<string, number>} */
    const updates = {};
    const kept = new Set();
    for (const line of giftLines) {
      const id = String(line.variant_id);
      if (!wanted.includes(id) || kept.has(id)) {
        updates[line.key] = 0;
      } else {
        kept.add(id);
        if (line.quantity !== 1) updates[line.key] = 1;
      }
    }

    const toAdd = wanted.filter((id) => !kept.has(id) && !memory.dismissed.includes(id) && !this.#failed.has(id));

    memory.seen = [...kept, ...toAdd];
    saveMemory(memory);

    if (Object.keys(updates).length) {
      await this.#send('update', { updates });
    }

    if (toAdd.length) {
      try {
        await this.#send('add', {
          items: toAdd.map((id) => ({ id: Number(id), quantity: 1, properties: { [GIFT_PROPERTY]: '1' } })),
        });
      } catch (error) {
        toAdd.forEach((id) => this.#failed.add(id));
        memory.seen = [...kept];
        saveMemory(memory);
        throw error;
      }
    }
  }

  /**
   * Envoie la requête au panier et prévient Horizon pour qu'il redessine le tiroir / la page panier.
   * L'événement est déclaré en « update » (et pas « add ») pour ne pas ouvrir le tiroir tout seul.
   * @param {'add' | 'update'} kind
   * @param {object} payload
   */
  async #send(kind, payload) {
    const routes = /** @type {any} */ (window).Theme?.routes ?? {};
    const base = kind === 'add' ? routes.cart_add_url ?? '/cart/add' : routes.cart_update_url ?? '/cart/update';
    const url = `${String(base).replace(/\.js$/, '')}.js`;
    const sections = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    /** @type {any} */
    const anyPayload = payload;
    const lines =
      kind === 'add'
        ? anyPayload.items.map((/** @type {any} */ item) => ({ merchandiseId: String(item.id), quantity: 1 }))
        : Object.entries(anyPayload.updates).map(([key, quantity]) => ({ id: key, quantity }));

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: sections.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cadeau : requête refusée');
      }
      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'mx-auto-gift',
          sections: data.sections,
          didError: false,
        },
      });
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }
}

if (!customElements.get('mx-auto-gift')) {
  customElements.define('mx-auto-gift', MxAutoGift);
}
